import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Badge as MuiBadge,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  RemoveRedEye,
  QuestionAnswer,
  BarChart,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import useTranslation from "hooks/Translation";

export default function ProductDetail({ productData, ViewsData }) {
  const t = useTranslation();
  const [selectedImage, setSelectedImage] = useState(0);
  const currency = getFromStorage(STORAGE_KEYS.currency);

  const product = {
    id: 1,
    name: "Premium Organic Apples",
    category: "Fruits",
    details: {
      grade: "abc",
      modalNo: "0022",
      shelfLife: "2 years",
      style: "fruit",
      productType: "fruit",
      placeOfOrigin: "Dubai",
      quantity: "5kg box",
      stock: 100,
      price: 24.99,
      packaging: "Eco-friendly cardboard box",
    },
    description:
      "Our premium organic apples are sourced from certified organic orchards in the scenic valleys of Washington state. These apples meet the highest quality standards and are certified organic by the USDA. They are picked at the peak of ripeness to ensure the best flavor and nutritional value. Handle with care and store in a cool, dry place for maximum freshness.",
    images:
      productData?.product_image?.length > 0
        ? productData?.product_image?.map((image) => image?.media_url)
        : [],

    metrics: {
      views: 1250,
      inquiries: 75,
      conversionRate: 20,
    },
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setSelectedImage(
      (prev) => (prev - 1 + product.images.length) % product.images.length
    );
  };

  const productImages = productData?.product_image?.map(
    (image) => image?.media_url
  );

  return (
    <MDBox sx={{ margin: "auto" }}>
      <CardHeader
        title={
          <MDBox>
            <MDTypography variant="h5">
              {productData?.product_name_en || "-"}
            </MDTypography>
            <Chip
              label={
                productData?.sub_category?.category?.category_name_en || "-"
              }
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </MDBox>
        }
        action={
          <MDBox display="flex" gap={2}>
            <Tooltip title="Number of views">
              <MDBox display="flex" alignItems="center" gap={1}>
                <RemoveRedEye fontSize="small" />
                <MDTypography>{product.metrics.views}</MDTypography>
              </MDBox>
            </Tooltip>
            <Tooltip title="Number of inquiries">
              <MDBox display="flex" alignItems="center" gap={1}>
                <QuestionAnswer fontSize="small" />
                <MDTypography>{product.metrics.inquiries}</MDTypography>
              </MDBox>
            </Tooltip>
            <Tooltip title="Conversion rate">
              <MDBox display="flex" alignItems="center" gap={1}>
                <BarChart fontSize="small" />
                <MDTypography>{product.metrics.conversionRate}%</MDTypography>
              </MDBox>
            </Tooltip>
          </MDBox>
        }
      />
      <CardContent>
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={4}
        >
          {/* Image Viewer */}
          <MDBox flex={1} sx={{ width: "50%" }}>
            <MDBox
              sx={{
                position: "relative",
                // overflow: "hidden",
                width: "100%", // Adjust size here
                height: "300px", // Adjust size here
                margin: "auto", // Center the image
                right: 10,
                bottom: 14,
              }}
            >
              <CardMedia
                component="img"
                image={product.images[selectedImage]}
                // alt={`Image ${selectedImage + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 25,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                  },
                }}
                onClick={prevImage}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 0,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                  },
                }}
                onClick={nextImage}
              >
                <ChevronRight />
              </IconButton>
            </MDBox>
            <MDBox display="flex" justifyContent="center" mt={2} gap={1}>
              {product.images.map((_, index) => (
                <MuiBadge
                  key={index}
                  color={selectedImage === index ? "primary" : "default"}
                  onClick={() => setSelectedImage(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                />
              ))}
            </MDBox>
          </MDBox>

          {/* Product Details */}
          <MDBox flex={1}>
            <MDBox
              mb={3}
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <MDTypography variant="h6">{t.main.productDetails}</MDTypography>
              <MDTypography variant="body2">
                {t.main.quantity}: {productData?.quantity_per_unit || "-"} KG
                <br />
                {t.main.availableStock}: {productData?.total_quantity || "-"}{" "}
                units
                <br />
                {t.main.pricePerUnit}: {currency === "USD" ? "USD" : "AED"}{" "}
                {productData?.price_per_unit}
                <br />
                {t.main.placeOfOrigin}:{" "}
                {productData?.place_of_origin_en
                  ? productData?.place_of_origin_en[0]
                  : productData?.place_of_origin_ar?.[0]}
                <br />
                {t.main.grade}: {productData?.grade_en || "-"}
                <br />
                {t.main.modalNumber}: {productData?.model_no_en || "-"}
                <br />
                {t.main.shelfLife}: {productData?.shell_life_en || "-"}
                <br />
                {t.main.productType}: {productData?.product_type_en || "-"}
              </MDTypography>
              <MDBox mt={4}>
                <MDTypography variant="h6">{t.main.productDesc}</MDTypography>
                <MDBox
                  sx={{
                    maxHeight: 150,
                    overflow: "auto",
                  }}
                >
                  <MDTypography variant="body2">
                    {productData?.product_desc_en || "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </CardContent>
    </MDBox>
  );
}
